// ── V158: 입고 기록 hook ───────────────────────────────────────────────────────
// users/{매장}/receivings/{id}       — 영수증 한 장 (최근 RECEIVINGS_LIMIT건을 실시간으로)
// users/{매장}/receivingPhotos/{id}  — 그 영수증 사진 (열 때만 읽는다)
// 직원(매니저) 계정도 쓰고 고칠 수 있다. 지우기는 사장님 계정만 (보안 규칙과 같음).
import { useCallback, useEffect, useState } from 'react';
import {
  collection, doc, setDoc, deleteDoc, getDoc, onSnapshot, query, orderBy, limit as fbLimit,
} from 'firebase/firestore';
import { db } from './db';
import { RECEIVINGS_LIMIT, clean } from './utils/receiving';

export function useReceivings(user, ownerUid) {
  const uid = ownerUid || user?.uid || null;
  const [receivings, setReceivings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!user || !uid) { setReceivings([]); setLoading(false); return undefined; }
    setLoading(true);
    const q = query(collection(db, 'users', uid, 'receivings'), orderBy('date', 'desc'), fbLimit(RECEIVINGS_LIMIT));
    const unsub = onSnapshot(q, (snap) => {
      const list = [];
      snap.forEach(d => list.push({ ...d.data(), id: d.id }));
      setReceivings(list);
      setError(null);
      setLoading(false);
    }, (e) => {
      console.error('receivings load error:', e);
      setError(e);
      setLoading(false);
    });
    return () => unsub();
  }, [user, uid]);

  const save = useCallback(async (rec) => {
    if (!uid || !rec?.id) throw new Error('no store');
    const { id, ...data } = clean(rec);
    await setDoc(doc(db, 'users', uid, 'receivings', id), data);
    return id;
  }, [uid]);

  const patch = useCallback(async (id, fields) => {
    if (!uid || !id) throw new Error('no store');
    await setDoc(doc(db, 'users', uid, 'receivings', id), clean(fields), { merge: true });
  }, [uid]);

  const remove = useCallback(async (rec) => {
    if (!uid || !rec?.id) throw new Error('no store');
    await deleteDoc(doc(db, 'users', uid, 'receivings', rec.id));
    if (rec.hasPhoto) {
      try { await deleteDoc(doc(db, 'users', uid, 'receivingPhotos', rec.id)); } catch (e) { console.warn('photo delete', e); }
    }
  }, [uid]);

  const savePhoto = useCallback(async (id, photo, who = '') => {
    if (!uid || !id || !photo?.data) throw new Error('no photo');
    await setDoc(doc(db, 'users', uid, 'receivingPhotos', id), clean({
      data: photo.data, w: photo.w, h: photo.h, bytes: photo.bytes, createdAt: Date.now(),
      createdBy: String(who || '').slice(0, 30) || undefined,
    }));
  }, [uid]);

  const loadPhoto = useCallback(async (id) => {
    if (!uid || !id) return null;
    const snap = await getDoc(doc(db, 'users', uid, 'receivingPhotos', id));
    return snap.exists() ? snap.data() : null;
  }, [uid]);

  const removePhoto = useCallback(async (id) => {
    if (!uid || !id) return;
    await deleteDoc(doc(db, 'users', uid, 'receivingPhotos', id));
  }, [uid]);

  return { receivings, loading, error, save, patch, remove, savePhoto, loadPhoto, removePhoto };
}
